import { useRef, useState, useEffect } from 'react';
import { usePaletteContext } from '../contexts/PaletteContext';
import { ExtractionSettingsPanel } from '../components/palette/ExtractionSettingsPanel';
import {
  paletteExtractor,
  type ExtractionAlgorithm,
  type BrandSafeOptions,
} from '../lib/paletteExtraction';
import { db } from '../lib/database';
import { Check, Link, AlertTriangle } from 'lucide-react';

function paletteNameFromFile(file: File): string {
  const base = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');
  return base.length > 30 ? base.slice(0, 30).trim() : base;
}

function paletteNameFromUrl(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const base = pathname.split('/').pop()?.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ') || 'URL extract';
    return base.length > 30 ? base.slice(0, 30).trim() : base;
  } catch {
    return 'URL extract';
  }
}

export function ExtractTool() {
  const { currentPalette, updateCurrentPalette, refreshPalettes, setCurrentPalette } = usePaletteContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [extractionK, setExtractionK] = useState(5);
  const [extractionAlgo, setExtractionAlgo] =
    useState<ExtractionAlgorithm>('kmeans');
  const [brandSafeEnabled, setBrandSafeEnabled] = useState(false);
  const [removeOutliers, setRemoveOutliers] = useState(true);
  const [outlierThreshold, setOutlierThreshold] = useState(50);
  const [contrastBias, setContrastBias] = useState(false);
  const [targetBackground, setTargetBackground] = useState('#ffffff');
  const [minLightness, setMinLightness] = useState<number | undefined>(
    undefined,
  );
  const [maxLightness, setMaxLightness] = useState<number | undefined>(
    undefined,
  );
  const [minSaturation, setMinSaturation] = useState<number | undefined>(
    undefined,
  );

  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedPreview, setExtractedPreview] = useState<string[] | null>(null);
  const [cameraAvailable, setCameraAvailable] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);

  useEffect(() => {
    navigator.mediaDevices?.enumerateDevices().then((devices) => {
      setCameraAvailable(devices.some((d) => d.kind === 'videoinput'));
    }).catch(() => setCameraAvailable(false));
  }, []);

  const getBrandSafeOptions = (): BrandSafeOptions | undefined =>
    brandSafeEnabled
      ? {
          removeOutliers,
          outlierThreshold,
          contrastBias,
          targetBackgroundHex: contrastBias ? targetBackground : undefined,
          minLightness,
          maxLightness,
          minSaturation,
        }
      : undefined;

  const applyExtractedColors = async (colors: string[], sourceName?: string) => {
    if (currentPalette) {
      await updateCurrentPalette({ colors: [...currentPalette.colors, ...colors] });
    } else {
      const name = sourceName || `Extracted palette`;
      const id = await db.addPalette({ name, colors, tags: [] });
      await refreshPalettes();
      const created = await db.getPalette(id);
      if (created) setCurrentPalette(created);
    }
    setExtractedPreview(null);
  };

  const extractFromImageData = async (imageData: ImageData, sourceName?: string) => {
    const extractedColors = await paletteExtractor.extractFromImage(
      imageData,
      extractionK,
      extractionAlgo,
      getBrandSafeOptions(),
    );
    if (extractedColors && extractedColors.length > 0) {
      setExtractedPreview(extractedColors);
    }
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setIsExtracting(true);
    setExtractedPreview(null);

    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };

    img.onload = async () => {
      try {
        const canvas = document.createElement('canvas');
        const maxSize = 800;
        let width = img.width;
        let height = img.height;

        if (width > height && width > maxSize) {
          height = (height / width) * maxSize;
          width = maxSize;
        } else if (height > maxSize) {
          width = (width / height) * maxSize;
          height = maxSize;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.drawImage(img, 0, 0, width, height);
        const imageData = ctx.getImageData(0, 0, width, height);
        await extractFromImageData(imageData, paletteNameFromFile(file));
      } catch (err) {
        console.error('Extraction failed:', err);
      } finally {
        setIsExtracting(false);
      }
    };

    img.onerror = () => {
      console.error('Image load failed');
      setIsExtracting(false);
    };

    reader.readAsDataURL(file);
  };

  const handleCameraExtraction = async () => {
    setIsExtracting(true);
    setExtractedPreview(null);
    try {
      const extractedColors = await paletteExtractor.extractFromCamera(
        extractionK,
        extractionAlgo,
        getBrandSafeOptions(),
      );
      if (extractedColors && extractedColors.length > 0) {
        setExtractedPreview(extractedColors);
      }
    } catch (err) {
      console.error('Camera extraction failed:', err);
    } finally {
      setIsExtracting(false);
    }
  };

  const handleUrlExtraction = async () => {
    const url = urlInput.trim();
    if (!url) return;

    setIsExtracting(true);
    setExtractedPreview(null);
    setUrlError(null);

    try {
      new URL(url); // validate
    } catch {
      setUrlError('Invalid URL. Please enter a full URL starting with https://');
      setIsExtracting(false);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = async () => {
      try {
        const canvas = document.createElement('canvas');
        const maxSize = 800;
        let width = img.width;
        let height = img.height;
        if (width > height && width > maxSize) {
          height = (height / width) * maxSize;
          width = maxSize;
        } else if (height > maxSize) {
          width = (width / height) * maxSize;
          height = maxSize;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, width, height);
        const imageData = ctx.getImageData(0, 0, width, height);
        await extractFromImageData(imageData, paletteNameFromUrl(url));
      } catch (err) {
        console.error('URL extraction failed:', err);
        setUrlError('Failed to read image pixels. The server may not allow cross-origin access (CORS).');
      } finally {
        setIsExtracting(false);
      }
    };

    img.onerror = () => {
      setUrlError('Could not load image. The URL may be invalid or the server blocks cross-origin requests.');
      setIsExtracting(false);
    };

    img.src = url;
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-6">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Color Extraction
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Extract colors from images, URLs, or your camera.
          {currentPalette
            ? ` Colors will be appended to "${currentPalette.name}".`
            : ' A new palette will be created automatically.'}
        </p>

        <ExtractionSettingsPanel
          extractionK={extractionK}
          extractionAlgo={extractionAlgo}
          brandSafeEnabled={brandSafeEnabled}
          removeOutliers={removeOutliers}
          outlierThreshold={outlierThreshold}
          contrastBias={contrastBias}
          targetBackground={targetBackground}
          minLightness={minLightness}
          maxLightness={maxLightness}
          minSaturation={minSaturation}
          onKChange={setExtractionK}
          onAlgoChange={setExtractionAlgo}
          onBrandSafeToggle={setBrandSafeEnabled}
          onRemoveOutliersToggle={setRemoveOutliers}
          onOutlierThresholdChange={setOutlierThreshold}
          onContrastBiasToggle={setContrastBias}
          onTargetBackgroundChange={setTargetBackground}
          onMinLightnessChange={setMinLightness}
          onMaxLightnessChange={setMaxLightness}
          onMinSaturationChange={setMinSaturation}
        />

        {/* URL Input (W-008) */}
        <div className="mt-4 mb-4 p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50">
          <div className="flex items-center gap-2 mb-2">
            <Link size={14} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Extract from URL</span>
          </div>
          <div className="flex gap-2">
            <input
              type="url"
              value={urlInput}
              onChange={(e) => { setUrlInput(e.target.value); setUrlError(null); }}
              placeholder="https://example.com/image.jpg"
              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-500 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              onKeyDown={(e) => { if (e.key === 'Enter') void handleUrlExtraction(); }}
            />
            <button
              onClick={() => void handleUrlExtraction()}
              disabled={!urlInput.trim() || isExtracting}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
            >
              Extract
            </button>
          </div>
          {urlError && (
            <div className="mt-2 flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
              <span>{urlError}</span>
            </div>
          )}
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
            Works with CORS-enabled sources (Unsplash, Pexels, CDNs). For other URLs, download the image first.
          </p>
        </div>

        <div className="mt-4 flex gap-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isExtracting}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            {isExtracting ? 'Extracting…' : 'Extract from Image'}
          </button>
          <button
            onClick={handleCameraExtraction}
            disabled={!cameraAvailable || isExtracting}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-gray-900 dark:text-gray-100 rounded-lg transition-colors"
          >
            Camera
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
          tabIndex={-1}
          aria-hidden="true"
        />

        {extractedPreview && (
          <div className="mt-6 border-t border-gray-200 dark:border-gray-600 pt-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Extracted {extractedPreview.length} colors
            </h3>
            <div className="flex gap-2 mb-4">
              {extractedPreview.map((hex, i) => (
                <div
                  key={i}
                  className="w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-600"
                  style={{ backgroundColor: hex }}
                  title={hex}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => applyExtractedColors(extractedPreview)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                <Check size={16} />
                {currentPalette ? 'Append to palette' : 'Create palette'}
              </button>
              <button
                onClick={() => setExtractedPreview(null)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              >
                Discard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
